/**
 * Staff portal — Calendar.
 *
 * Combines three event streams onto a single month view:
 *  - My leaves (approved + pending) coloured by leave-type colour
 *  - Public holidays (cross-tenant catalogue from System service)
 *  - Birthdays (privacy-aware: first name + last initial; opt-out honoured server-side)
 *
 * Filter chips at the top toggle each stream.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Group,
  Card,
  Chip,
  Badge,
  Button,
  Modal,
  TextInput,
  CopyButton,
  Tooltip,
  ActionIcon,
  Loader,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCalendarPlus, IconCopy, IconCheck, IconRefresh } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import { EventCalendar, type CalendarEvent } from '@/components/advanced/calendar/EventCalendar';
import {
  useStaffPortalLeaveService,
  useStaffPortalCalendarService,
  usePublicHolidayService,
} from '@actschurch/ccms-sdk';
import { hrKeys } from '../utils/queryKeys';
import { EmployeeQuickViewProvider, useEmployeeQuickView } from '../components';

type StreamKey = 'leaves' | 'holidays' | 'birthdays';

export default function EmployeeCalendarPage() {
  return (
    <EmployeeQuickViewProvider>
      <CalendarBoard />
    </EmployeeQuickViewProvider>
  );
}

function CalendarBoard() {
  const navigate = useNavigate();
  const openEmployee = useEmployeeQuickView();
  const leaveService = useStaffPortalLeaveService();
  const calendarService = useStaffPortalCalendarService();
  const holidayService = usePublicHolidayService();

  const [streams, setStreams] = useState<StreamKey[]>(['leaves', 'holidays', 'birthdays']);
  const queryClient = useQueryClient();
  const [subscribeOpened, { open: openSubscribe, close: closeSubscribe }] = useDisclosure(false);

  // Lazily fetch the subscribe-able ICS feed URL only when the modal opens.
  const { data: feed, isLoading: feedLoading } = useQuery({
    queryKey: ['hr', 'calendar', 'feed'],
    queryFn: () => calendarService.getCalendarFeed(),
    enabled: subscribeOpened,
  });
  const rotateFeed = useMutation({
    mutationFn: () => calendarService.rotateCalendarFeed(),
    onSuccess: (data) => queryClient.setQueryData(['hr', 'calendar', 'feed'], data),
  });
  const feedUrl = feed?.url ?? '';

  const { data: myLeaves = [] } = useQuery({
    queryKey: hrKeys.myLeaves(),
    queryFn: () => leaveService.getMyLeaves(),
  });

  const { data: holidays = [] } = useQuery({
    queryKey: hrKeys.publicHolidays(false),
    queryFn: () => holidayService.getByWindow(),
  });

  const { data: birthdays = [] } = useQuery({
    queryKey: hrKeys.birthdays(),
    queryFn: () => calendarService.getBirthdays(),
  });

  const events: CalendarEvent[] = useMemo(() => {
    const list: CalendarEvent[] = [];

    if (streams.includes('leaves')) {
      for (const l of myLeaves as any[]) {
        if (!l.startDate || !l.endDate) continue;
        list.push({
          id: `leave-${l.leaveID}`,
          title: l.typeName ? `${l.typeName} (${l.statusName ?? ''})` : 'Leave',
          start: new Date(l.startDate),
          end: new Date(l.endDate),
          allDay: true,
          color: l.typeColor ?? '#3B82F6',
          category: 'leave',
          metadata: { kind: 'leave', leaveID: l.leaveID },
        });
      }
    }

    if (streams.includes('holidays')) {
      for (const h of holidays as any[]) {
        if (!h.date) continue;
        const d = new Date(h.date);
        list.push({
          id: `holiday-${h.holidayID}`,
          title: h.name,
          start: d,
          end: d,
          allDay: true,
          color: '#F59E0B',
          category: 'holiday',
          metadata: { kind: 'holiday', holidayID: h.holidayID },
        });
      }
    }

    if (streams.includes('birthdays')) {
      const yr = new Date().getFullYear();
      for (const b of birthdays as any[]) {
        // Year-agnostic projection onto the current calendar year.
        const day = new Date(yr, (b.birthMonth ?? 1) - 1, b.birthDay ?? 1);
        list.push({
          id: `bday-${b.memberID}`,
          title: `🎂 ${b.firstName} ${b.lastName ?? b.lastInitial ?? ''}`.trim(),
          start: day,
          end: day,
          allDay: true,
          color: b.isBirthdayToday ? '#EC4899' : '#A78BFA',
          category: 'birthday',
          metadata: { kind: 'birthday', userId: b.userId, memberID: b.memberID, isToday: b.isBirthdayToday },
        });
      }
    }

    return list;
  }, [streams, myLeaves, holidays, birthdays]);

  const toggleStream = (k: StreamKey) =>
    setStreams((curr) => (curr.includes(k) ? curr.filter((x) => x !== k) : [...curr, k]));

  const onEventClick = (ev: CalendarEvent) => {
    const meta = ev.metadata as { kind?: string; leaveID?: number; userId?: string } | undefined;
    if (meta?.kind === 'leave' && meta.leaveID) {
      navigate(`/app/members/my-leaves/${meta.leaveID}`);
    } else if (meta?.kind === 'birthday' && meta.userId) {
      openEmployee(meta.userId);
    }
  };

  return (
    <FadeIn>
      <Stack gap="md" p="md" maw={1280} mx="auto">
        <Group justify="space-between" wrap="wrap" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>Calendar</Title>
            <Text size="sm" c="dimmed">
              Your leaves, public holidays, and team birthdays — all in one view.
            </Text>
          </Stack>
          <PageHelpButton pageKey="hr.calendar" />
        </Group>

        <Card withBorder padding="md" radius="md" data-help-id="hr.calendar.toolbar">
          <Group justify="space-between" wrap="wrap">
            <Group gap="sm" data-help-id="hr.calendar.streams">
              <Chip checked={streams.includes('leaves')} onChange={() => toggleStream('leaves')} color="blue" variant="light">
                My leaves
              </Chip>
              <Chip checked={streams.includes('holidays')} onChange={() => toggleStream('holidays')} color="orange" variant="light">
                Public holidays
              </Chip>
              <Chip checked={streams.includes('birthdays')} onChange={() => toggleStream('birthdays')} color="pink" variant="light">
                Birthdays
              </Chip>
            </Group>
            <Group gap="xs">
              <Badge variant="dot" color="blue">Leaves</Badge>
              <Badge variant="dot" color="orange">Holidays</Badge>
              <Badge variant="dot" color="pink">Birthdays</Badge>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconCalendarPlus size={16} />}
                onClick={openSubscribe}
                data-help-id="hr.calendar.subscribe"
              >
                Subscribe
              </Button>
            </Group>
          </Group>
        </Card>

        <Modal
          opened={subscribeOpened}
          onClose={closeSubscribe}
          title="Subscribe to your calendar"
          centered
          size="lg"
        >
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Add a live, always-up-to-date feed of your approved leave and public
              holidays to Apple Calendar, Google Calendar or Outlook. Copy this link
              and add it as a subscribed/Internet calendar.
            </Text>
            {feedLoading ? (
              <Group justify="center" p="md"><Loader size="sm" /></Group>
            ) : (
              <Group gap="xs" wrap="nowrap">
                <TextInput
                  readOnly
                  value={feedUrl}
                  style={{ flex: 1 }}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <CopyButton value={feedUrl} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy link'} withArrow>
                      <ActionIcon variant="light" color={copied ? 'teal' : 'gray'} size="lg" onClick={copy} disabled={!feedUrl}>
                        {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            )}
            <Group justify="space-between" mt="xs">
              <Text size="xs" c="dimmed">
                Anyone with this link can view your leave dates — keep it private.
              </Text>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconRefresh size={14} />}
                loading={rotateFeed.isPending}
                onClick={() => rotateFeed.mutate()}
              >
                Generate new link
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Card withBorder padding="md" radius="md" data-help-id="hr.calendar.calendar">
          <EventCalendar events={events} onEventClick={onEventClick} />
        </Card>
      </Stack>
    </FadeIn>
  );
}
